import { useMemo, useRef } from "react";
import { Scheduler } from "@aldabil/react-scheduler";
import { pl } from "date-fns/locale";
import dayjs from "dayjs";
import { useCalendarIssues } from "components/CalendarIssues/context";
import calendar_translations from "helpers/calendar_translations";

const MonthScheduler = () => {
  const {
    tasks,
    events,
    view_type,
    holidays,
    calendar_date,
    setViewType,
    setCalendarDate,
  } = useCalendarIssues();
  const scheduler_ref = useRef();

  const summary = useMemo(() => {
    const grouped = {};

    // Tasks
    tasks.forEach(({ date }) => {
      const key = dayjs(date).format("YYYY-MM-DD");

      if (!grouped[key]) {
        grouped[key] = {
          date: key,
          issues_count: 0,
          task_count: 0,
        };
      }

      grouped[key].task_count += 1;
    });

    // Events (issues)
    events.forEach(({ service_date }) => {
      const key = dayjs(service_date).format("YYYY-MM-DD");

      if (!grouped[key]) {
        grouped[key] = {
          date: key,
          issues_count: 0,
          task_count: 0,
        };
      }

      grouped[key].issues_count += 1;
    });

    return Object.values(grouped).sort(
      (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf()
    );
  }, [tasks, events]);

  const data = useMemo(() => {
    return summary.map(({ date, issues_count, task_count }) => ({
      event_id: date,
      start: dayjs(date).toDate(),
      end: dayjs(date).toDate(),
      title: (
        <>
          {issues_count} - zgłoszenia <br />
          {task_count} - priorytety
        </>
      ),
    }));
  }, [summary]);

  const month_name = calendar_date
    ? dayjs(calendar_date).format("MMMM YYYY")
    : "-";

  const handleChangeDate = (date) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setCalendarDate(date);
    setViewType("day");
  };

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `:root {
            --month-value: "${month_name}";
          }`,
        }}
      />
      <div className="month-calendar">
        <Scheduler
          ref={scheduler_ref}
          key={[view_type, calendar_date]}
          agenda={false}
          disableViewer={true}
          locale={pl}
          height={800}
          selectedDate={calendar_date}
          view="month"
          deletable={false}
          draggable={false}
          editable={false}
          hourFormat={24}
          events={data}
          month={{
            weekStartOn: 1,
            cellRenderer: ({ start, end, day, ...rest }) => {
              const is_today =
                dayjs().format("DD-MM") === dayjs(start).format("DD-MM");
              const week_day = dayjs(start).format("dd");
              const is_weekend = ["So", "Nd"].includes(week_day);
              const is_same_month =
                dayjs(day).format("MM") === dayjs(start).format("MM") ||
                dayjs(day).format("MM") === dayjs(end).format("MM");

              const day_holidays = holidays.filter(
                ({ date }) => date === dayjs(start).format("YYYY-MM-DD")
              );
              const is_holiday = day_holidays?.length > 0;

              return (
                <div
                  {...rest}
                  className={`month-cell${is_today ? " today" : ""}${
                    is_holiday ? " holiday" : ""
                  }${is_weekend ? " weekend" : ""} ${
                    !is_same_month ? "disabled" : ""
                  }`}
                >
                  <span className="month-cell__day">
                    {dayjs(start).format("DD")}
                  </span>
                </div>
              );
            },
          }}
          disableViewNavigator={true}
          onSelectedDateChange={(date) => setCalendarDate(dayjs(date).toDate())}
          translations={calendar_translations}
          onCellClick={(cell_date) =>
            handleChangeDate(dayjs(cell_date).toDate())
          }
          onEventClick={({ start }) => handleChangeDate(dayjs(start).toDate())}
        />
      </div>
    </>
  );
};

export default MonthScheduler;
